/**
 * Reading an Excel workbook, by hand.
 *
 * An `.xlsx` is a ZIP of XML files. `zip.ts` next door already writes one by
 * hand for the CSV export, for the reason stated there — every dependency is a
 * supply-chain surface in an app that holds clinical records — and this reads
 * one back on the same terms.
 *
 * The scope is deliberately narrow. The importer downstream treats every cell
 * as a string, so this needs sheet names, the shared string table, and cell
 * text. It does not need styles, formulas, charts or formatting, and does not
 * look at them.
 *
 * Everything happens in the browser. The workbook is never uploaded, which is
 * the same promise the CSV path makes and the reason the import screen says so.
 */

const ZIP_CENTRAL_SIGNATURE = 0x02014b50;
const ZIP_EOCD_SIGNATURE = 0x06054b50;
const STORED = 0;
const DEFLATED = 8;

export class WorkbookError extends Error {}

type ZipEntry = { name: string; bytes: Uint8Array };

/**
 * Finds the end-of-central-directory record, which is the only fixed landmark
 * in a ZIP and sits at the very end — unless there is a trailing comment, so it
 * is searched for backwards rather than assumed.
 */
function findEocd(view: DataView): number {
  const from = Math.max(0, view.byteLength - 66_000);

  for (let offset = view.byteLength - 22; offset >= from; offset -= 1) {
    if (view.getUint32(offset, true) === ZIP_EOCD_SIGNATURE) return offset;
  }

  throw new WorkbookError('That file is not a valid Excel workbook.');
}

async function inflate(bytes: Uint8Array, method: number): Promise<Uint8Array> {
  if (method === STORED) return bytes;

  if (method !== DEFLATED) {
    throw new WorkbookError('That workbook uses a compression this app cannot read.');
  }

  /*
   * `deflate-raw` rather than `deflate`: ZIP stores the deflate payload with no
   * zlib header, and asking for the wrong one fails on every entry. Native in
   * the browser, so nothing is bundled to do this.
   */
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(
    new DecompressionStream('deflate-raw'),
  );

  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Every file inside the workbook, by name. */
async function readZip(buffer: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const eocd = findEocd(view);

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);

  const entries = new Map<string, Uint8Array>();

  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(offset, true) !== ZIP_CENTRAL_SIGNATURE) break;

    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);

    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLength));

    // The local header repeats the name and extra fields, and its own lengths
    // are the ones that count — some writers disagree with the central copy.
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLength + localExtraLength;

    const entry: ZipEntry = {
      name,
      bytes: bytes.subarray(dataStart, dataStart + compressedSize),
    };

    entries.set(entry.name, await inflate(entry.bytes, method));
    offset += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

function textOf(entries: Map<string, Uint8Array>, name: string): string | null {
  const found = entries.get(name);
  return found === undefined ? null : new TextDecoder().decode(found);
}

function parseXml(xml: string): Document {
  const document = new DOMParser().parseFromString(xml, 'application/xml');

  if (document.querySelector('parsererror') !== null) {
    throw new WorkbookError('That workbook could not be read.');
  }

  return document;
}

/**
 * `B7` → 1. The column letters only; the row number is ignored because rows
 * arrive in order and gaps in them do not matter.
 *
 * Cells are only written where they hold something, so a row can jump from A to
 * D. Without this the columns would slide left and every value would land under
 * the wrong header — the kind of failure that looks like the file is wrong.
 */
function columnIndex(reference: string): number {
  const letters = /^([A-Z]+)/.exec(reference.toUpperCase())?.[1] ?? '';

  let index = 0;
  for (const letter of letters) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }

  return index - 1;
}

export type WorkbookSheet = {
  name: string;
  /** Row 1 first. Ragged rows are padded so every row is the same width. */
  rows: string[][];
};

/** The shared string table, which most text cells point into rather than repeat. */
function sharedStrings(entries: Map<string, Uint8Array>): string[] {
  const xml = textOf(entries, 'xl/sharedStrings.xml');
  if (xml === null) return [];

  return [...parseXml(xml).getElementsByTagName('si')].map((si) =>
    // A string can be split across several runs when part of it is styled.
    [...si.getElementsByTagName('t')].map((t) => t.textContent ?? '').join(''),
  );
}

function cellText(cell: Element, strings: readonly string[]): string {
  const type = cell.getAttribute('t');

  if (type === 'inlineStr') {
    return [...cell.getElementsByTagName('t')].map((t) => t.textContent ?? '').join('');
  }

  const value = cell.getElementsByTagName('v')[0]?.textContent ?? '';
  if (value === '') return '';

  if (type === 's') {
    return strings[Number(value)] ?? '';
  }

  if (type === 'b') return value === '1' ? 'TRUE' : 'FALSE';

  /*
   * Anything else is a number, and dates are numbers in Excel — 42736 is the
   * 1st of January 2017. Deliberately handed on as the raw number rather than
   * decoded here: `parseImportedDate` recognises a date serial, which keeps
   * this file out of the business of reading style records to find out which
   * numbers are dates.
   */
  return value;
}

function sheetRows(xml: string, strings: readonly string[]): string[][] {
  const rows: string[][] = [];

  for (const row of parseXml(xml).getElementsByTagName('row')) {
    const cells: string[] = [];

    for (const cell of row.getElementsByTagName('c')) {
      const reference = cell.getAttribute('r');
      const index = reference === null ? cells.length : columnIndex(reference);

      while (cells.length < index) cells.push('');
      cells[index] = cellText(cell, strings);
    }

    rows.push(cells);
  }

  const width = rows.reduce((widest, row) => Math.max(widest, row.length), 0);
  return rows.map((row) => {
    const padded = [...row];
    while (padded.length < width) padded.push('');
    return padded;
  });
}

/**
 * Every sheet in the workbook, in the order the tabs appear.
 *
 * Sheet order and names live in `workbook.xml`, but the file each one is
 * actually stored in comes from the relationships file beside it — the names
 * `sheet1.xml`, `sheet2.xml` do **not** reliably match tab order, and assuming
 * they do silently imports the wrong tab.
 */
export async function readWorkbook(file: File | ArrayBuffer): Promise<WorkbookSheet[]> {
  const buffer = file instanceof ArrayBuffer ? file : await file.arrayBuffer();
  const entries = await readZip(buffer);

  const workbookXml = textOf(entries, 'xl/workbook.xml');
  if (workbookXml === null) {
    throw new WorkbookError('That file is not an Excel workbook.');
  }

  const relsXml = textOf(entries, 'xl/_rels/workbook.xml.rels') ?? '<Relationships/>';
  const targets = new Map<string, string>();

  for (const relationship of parseXml(relsXml).getElementsByTagName('Relationship')) {
    const id = relationship.getAttribute('Id');
    const target = relationship.getAttribute('Target');
    if (id !== null && target !== null) targets.set(id, target.replace(/^\/?xl\//, ''));
  }

  const strings = sharedStrings(entries);
  const sheets: WorkbookSheet[] = [];

  for (const sheet of parseXml(workbookXml).getElementsByTagName('sheet')) {
    const name = sheet.getAttribute('name') ?? '';
    const id = sheet.getAttribute('r:id') ?? sheet.getAttributeNS(null, 'id') ?? '';
    const target = targets.get(id);
    if (target === undefined) continue;

    const xml = textOf(entries, `xl/${target}`);
    if (xml === null) continue;

    sheets.push({ name, rows: sheetRows(xml, strings) });
  }

  if (sheets.length === 0) throw new WorkbookError('That workbook has no readable sheets.');

  return sheets;
}

/** A sheet as the importer wants it: header row, then a record per row. */
export function sheetToRows(sheet: WorkbookSheet): {
  headers: string[];
  rows: Record<string, string>[];
} {
  /*
   * Headers are usually row 1, but the IMT logbook template prints a title
   * ("Log of procedures - Internal Medicine Training (IMT)") into a single
   * cell above a blank spacer row before the real header row. Neither carries
   * more than one populated cell — no real header row in either logbook this
   * app knows of does that — so leading rows this sparse are skipped rather
   * than read as headers.
   */
  const start = sheet.rows.findIndex((row) => row.filter((cell) => cell.trim() !== '').length >= 2);
  const [header = [], ...body] = start === -1 ? sheet.rows : sheet.rows.slice(start);
  const headers = header.map((cell) => cell.trim());

  const rows = body
    .filter((row) => row.some((cell) => cell.trim() !== ''))
    .map((row) =>
      Object.fromEntries(headers.map((name, index) => [name, (row[index] ?? '').trim()])),
    );

  return { headers, rows };
}
